import React, { useMemo } from "react";
import { LogOut, Menu } from "lucide-react";
import { useLocation } from "react-router-dom";

import {
  clearAuthSession,
  getAuthToken,
  getUserName,
  getUserRole,
} from "services/storage";
import { API_PREFIX } from "config/env";

const pageTitles = [
  [/^\/$/, "Dashboard"],
  [/^\/patients\/create-slip\//, "Create Prescription"],
  [/^\/patients\/[^/]+\/create-estimate$/, "New Treatment Estimate"],
  [/^\/treatment-estimates\/[^/]+\/edit$/, "Edit Treatment Estimate"],
  [/^\/treatment-estimates\/[^/]+$/, "Treatment Estimate"],
  [/^\/patients\/edit\//, "Edit Patient"],
  [/^\/patients\//, "Patient Details"],
  [/^\/patients$/, "Patients"],
  [/^\/create-patient$/, "New Patient"],
  [/^\/appointment-details\//, "Appointment Details"],
  [/^\/book-appointment$/, "Appointments"],
  [/^\/appointments$/, "Appointment Slots"],
  [/^\/create-doctor$/, "New Doctor"],
  [/^\/doctors$/, "Doctors"],
  [/^\/enquiries$/, "Enquiries"],
  [/^\/reviews$/, "Reviews"],
  [/^\/blogs$/, "Blogs"],
  [/^\/mails$/, "Email"],
  [/^\/register$/, "Registrations"],
  [/^\/colorsettings$/, "Appearance"],
  [/^\/addons$/, "Medicines & Diseases"],
  [/^\/inventory$/, "Medicine Stocks"],
  [/^\/lab$/, "Investigations"],
  [/^\/accounts$/, "Accounts"],
  [/^\/audit-logs$/, "Manager Activity"],
  [/^\/admin\/webinar-registrations$/, "Webinar Leads"],
];

const Navbar = ({ toggleSidebar }) => {
  const { pathname } = useLocation();
  const userName = getUserName() || "User";
  const role = getUserRole() || "Staff";

  const pageTitle = useMemo(() => {
    return pageTitles.find(([pattern]) => pattern.test(pathname))?.[1] || "BMSD CRM";
  }, [pathname]);

  const handleLogout = async () => {
    const token = getAuthToken();
    try {
      if (token) {
        await fetch(`${API_PREFIX}/auth/logout`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } finally {
      clearAuthSession();
      window.location.assign("/login");
    }
  };

  return (
    <header className="sticky top-0 z-40 border-b border-slate-200/90 bg-white/90 backdrop-blur-xl">
      <div className="flex h-[72px] items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            onClick={toggleSidebar}
            className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-slate-200 bg-white text-slate-600 shadow-sm hover:bg-[var(--accentColorLighter)] hover:text-slate-950 lg:hidden"
            aria-label="Open sidebar"
          >
            <Menu size={20} />
          </button>

          <div className="min-w-0">
            <p className="hidden text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-400 sm:block">
              BMSD Healthcare
            </p>
            <h1 className="truncate text-lg font-semibold tracking-tight text-slate-950">
              {pageTitle}
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <div className="hidden items-center gap-3 rounded-2xl border border-slate-200 bg-white py-1.5 pl-1.5 pr-3 sm:flex">
            <span className="crm-primary grid h-9 w-9 place-items-center rounded-xl text-sm font-semibold text-white">
              {userName.charAt(0).toUpperCase()}
            </span>
            <span className="min-w-0 leading-tight">
              <strong className="block max-w-36 truncate text-sm font-semibold text-slate-800">
                {userName}
              </strong>
              <span className="block text-[11px] capitalize text-slate-500">
                {role}
              </span>
            </span>
          </div>

          <button
            type="button"
            onClick={handleLogout}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-600 shadow-sm hover:border-red-200 hover:bg-red-50 hover:text-red-600"
            title="Sign out"
          >
            <LogOut size={18} />
            <span className="hidden md:inline">Sign out</span>
          </button>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
